import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton, useTheme, TextField, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, useMediaQuery, DialogContentText, MenuItem } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { tokens } from '../../../theme';
import Header from "../../../components/Header";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { addTerms, deleteTerms, editTerms, getTerms } from '../../../redux/slice/Terms.slice';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

const TermsDataGrid = ({ data, columns }) => {
    if (!data || !Array.isArray(data)) {
        return <div>No data availble...</div>;
    }
    return (
        <DataGrid
            rows={data}
            columns={columns}
            components={{ Toolbar: GridToolbar }}
            pageSize={5}
            rowsPerPageOptions={[5, 10]}
            getRowId={(row) => row._id}
            density="compact"
            autoHeight
            sx={{
                "@media print": {
                    "& .MuiDataGrid-toolbarContainer": {
                        display: "none !important"
                    },
                    "& .MuiDataGrid-root": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-cell": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-footerContainer": {
                        color: "black !important",
                    },
                    "& .css-7ms3qr-MuiTablePagination-displayedRows": {
                        color: "black !important",
                    },
                },
            }}
        />
    );
};

const Addterms = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const colors = tokens(theme.palette.mode);
    const [open, setOpen] = useState(false);
    const [update, setUpdate] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteRoleData, setDeleteRoleData] = useState(null);
    const terms = useSelector(state => state.Terms.Terms);
    const [openView, setOpenView] = React.useState(false);
    const [viewData, setViewData] = useState(null);
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery('(max-width:500px)');


    useEffect(() => {
        dispatch(getTerms());
    }, []);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        formik.resetForm();
        setUpdate(null);
    };

    const handleDelete = (id) => {
        setDeleteRoleData(id);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = () => {
        if (deleteRoleData) {
            dispatch(deleteTerms(deleteRoleData));
            setDeleteConfirmOpen(false);
            setDeleteRoleData(null);
        }
    }
    const cancelDelete = () => {
        setDeleteConfirmOpen(false);
        setDeleteRoleData(null);
    }

    const handleEdit = (data) => {
        formik.setValues(data);
        setOpen(true);
        setUpdate(data._id);
    };

    const handleClickOpenView = () => {
        setOpenView(true);
    };

    const handleCloseView = () => {
        setOpenView(false);
    };

    const handleView = (data) => {
        handleClickOpenView();
        setViewData(data)
    }

    const columns = useMemo(() => [
        { field: 'name', headerName: 'Name', width: 200 },
        { field: 'TermsType', headerName: 'Terms type', width: 150 },
        {
            field: 'description',
            headerName: 'Description',
            width: 1200,
            renderCell: (params) => (
                <div style={{
                    width: '100%',
                    padding: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {params.value}
                </div>
            )
        },
        {
            field: 'Action',
            headerName: 'Action',
            width: 130,
            renderCell: (params) => (
                <>
                    <IconButton
                        aria-label="view"
                        onClick={() => handleView(params.row)}
                    >
                        <RemoveRedEyeIcon />
                    </IconButton>
                    <IconButton aria-label="edit" onClick={() => handleEdit(params.row)}>
                        <EditIcon />
                    </IconButton>
                    <IconButton aria-label="delete" onClick={() => handleDelete(params.row._id)}>
                        <DeleteIcon />
                    </IconButton>
                </>
            )
        }
    ], []);

    const termsSchema = yup.object({
        name: yup.string().required("Please enter name"),
        description: yup.string().required("Please enter description"),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            description: '',
            TermsType: '',
        },
        validationSchema: termsSchema,
        onSubmit: (values, { resetForm }) => {
            if (update) {
                dispatch(editTerms({
                    _id: update,
                    name: values.name,
                    description: values.description,
                    TermsType: values.TermsType,
                }));
            } else {
                console.log(values);
                dispatch(addTerms(values));
            }
            resetForm();
            handleClose();
        }
    });

    const { handleSubmit, handleChange, handleBlur, values, errors, touched } = formik;

    return (
        <Box m="2px">
            <Header
                title="TERMS & CONDITIONS"
                subtitle="Manage Your Terms & Condition Here"
            />
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleClickOpen}>
                    Add Terms & Condition

                </Button>
            </Box>

            <Dialog open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                sx={{ '& .MuiDialog-paper': { width: '600px', height: '450px' } }}
            >
                <DialogTitle>{update ? 'Edit Terms & Condition' : 'Add Terms & Condition'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <TextField
                            margin="dense"
                            id="name"
                            name="name"
                            label="Enter Terms Name"
                            type="text"
                            fullWidth
                            variant="filled"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.name}
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            InputLabelProps={{
                                style: {
                                    color: theme.palette.text.primary
                                }
                            }}
                        />

                        <TextField
                            margin="dense"
                            id="TermsType"
                            name="TermsType"
                            label="Terms Type :"
                            select
                            fullWidth
                            variant="filled"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values?.TermsType}
                            error={touched.TermsType && Boolean(errors.TermsType)}
                            helperText={touched.TermsType && errors.TermsType}
                            InputLabelProps={{
                                style: {
                                    color: theme.palette.text.primary
                                }
                            }}
                            sx={{ width: '100%' }}
                        >
                            <MenuItem value="Sales">Sales</MenuItem>
                            <MenuItem value="Purchase">Purchase</MenuItem>
                        </TextField>

                        <FormControl variant="filled" sx={{ gridColumn: 'span 1', marginTop: '10px', width: '100%' }}>
                            <TextField
                                id="description"
                                fullWidth
                                multiline
                                rows={6}
                                variant="filled"
                                name="description"
                                label="Enter Description"
                                type="text"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.description}
                                error={touched.description && Boolean(errors.description)}
                                helperText={touched.description && errors.description}
                                InputLabelProps={{
                                    style: {
                                        color: theme.palette.text.primary
                                    }
                                }}
                            />
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} style={{ color: theme.palette.text.primary }}>Cancel</Button>
                        <Button type="submit" style={{ color: theme.palette.text.primary }}>{update ? 'Update' : 'Add'}</Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Box
                height="75vh"
                sx={useMemo(() => ({
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },

                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[700],
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiCheckbox-root": {
                        color: `${colors.greenAccent[200]} !important`,
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.grey[100]} !important`,
                        fontSize: "13px !important",
                    },

                }), [colors])}
            >
                <TermsDataGrid data={terms} columns={columns} />
            </Box>

            <Dialog
                fullScreen={fullScreen}
                open={openView}
                onClose={handleCloseView}
                aria-labelledby="responsive-dialog-title"
                maxWidth={"sm"}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title" style={{ backgroundColor: theme.palette.background.purple, textAlign: "center", fontWeight: 'bold', fontSize: '18px' }}>
                    {"Terms & Condition Details"}
                </DialogTitle>
                <DialogContent style={{ backgroundColor: theme.palette.background.purple }}>
                    <DialogContentText>
                        <div>
                            <div>
                                <div>
                                    <p style={{
                                        backgroundColor: theme.palette.background.light,
                                        marginBottom: '2px',
                                        padding: '10px'
                                    }}>
                                        <strong>Name : </strong>
                                        <span style={{ wordBreak: 'break-word' }}>{viewData?.name}</span>
                                    </p>
                                    <p style={{
                                        backgroundColor: theme.palette.background.light,
                                        marginBottom: '2px',
                                        padding: '10px'
                                    }}>
                                        <strong>Description : </strong>
                                        <span style={{
                                            wordBreak: 'break-word',
                                            whiteSpace: 'pre-wrap',
                                            display: 'inline-block'
                                        }}>{viewData?.description}</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ backgroundColor: theme.palette.background.purple }}>
                    <Button autoFocus onClick={handleCloseView} style={{ color: theme.palette.text.primary }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteConfirmOpen} onClose={cancelDelete}>
                <DialogContent>
                    Are you sure you want to delete this Terms & Condition?
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete} style={{ color: theme.palette.text.primary }}>No</Button>
                    <Button onClick={confirmDelete} style={{ color: theme.palette.text.primary }}>Yes</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Addterms;
